import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Info } from 'lucide-react'
import type { CommunityInput, SavedCommunity } from '@creanizer/types'

interface CommunityContextFormProps {
  input: CommunityInput
  onChange: (input: CommunityInput) => void
  savedCommunities?: SavedCommunity[]
}

export function CommunityContextForm({
  input,
  onChange,
  savedCommunities = [],
}: CommunityContextFormProps) {
  const handleCommunitySelect = (communityId: string) => {
    if (communityId === 'none') {
      // 「なし」が選択された場合
      onChange({
        ...input,
        relatedCommunityId: undefined,
        relatedCommunityContext: undefined,
        communityRelationship: undefined
      })
      return
    }
    const community = savedCommunities.find(c => c.id === communityId)
    if (community) {
      // コミュニティのコンテキストを生成（全データをJSONで渡す）
      const context = JSON.stringify(community.result)
      onChange({
        ...input,
        relatedCommunityId: communityId,
        relatedCommunityContext: context,
        // 関係性は選択後に設定してもらう
      })
    }
  }

  const handleRelationshipChange = (relationship: string) => {
    onChange({
      ...input,
      communityRelationship: relationship
    })
  }

  return (
    <div className="space-y-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          既存のコミュニティと関連付けることで、所属関係、敵対関係、同盟関係などを持つコミュニティを生成できます。
          例：国に所属する町、敵対する隣国、交易相手の都市など。
        </AlertDescription>
      </Alert>

      {/* 関連コミュニティ選択 */}
      <div className="space-y-2">
        <Label htmlFor="related-community">関連コミュニティを選択</Label>
        <Select
          value={input.relatedCommunityId || 'none'}
          onValueChange={handleCommunitySelect}
        >
          <SelectTrigger id="related-community">
            <SelectValue placeholder="関連コミュニティを選択（省略可）" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">なし</SelectItem>
            {savedCommunities.map(community => (
              <SelectItem key={community.id} value={community.id}>
                {community.result.basicInfo.name} ({community.result.basicInfo.type} / 人口: {community.result.basicInfo.population?.toLocaleString() || '不明'}人)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* 関係性の種類選択 */}
      {input.relatedCommunityId && (
        <div className="space-y-2">
          <Label htmlFor="relationship">関係性の種類</Label>
          <Select
            value={input.communityRelationship || ''}
            onValueChange={handleRelationshipChange}
          >
            <SelectTrigger id="relationship">
              <SelectValue placeholder="関係性を選択してください" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="parent">所属（この国/地域に属する）</SelectItem>
              <SelectItem value="child">包含（この中に含まれる）</SelectItem>
              <SelectItem value="rival">敵対関係</SelectItem>
              <SelectItem value="ally">同盟関係</SelectItem>
              <SelectItem value="trade">交易相手</SelectItem>
              <SelectItem value="neighbor">隣接地域</SelectItem>
              <SelectItem value="colony">植民地/属領</SelectItem>
              <SelectItem value="overlord">宗主国/支配者</SelectItem>
              <SelectItem value="historical">歴史的関係（かつて所属等）</SelectItem>
              <SelectItem value="cultural">文化的つながり</SelectItem>
              <SelectItem value="religious">宗教的つながり</SelectItem>
              <SelectItem value="custom">その他（詳細は追加設定で）</SelectItem>
            </SelectContent>
          </Select>
          {input.communityRelationship && (
            <p className="text-xs text-gray-500">
              選択された関係性: {
                input.communityRelationship === 'parent' ? '所属関係' :
                input.communityRelationship === 'child' ? '包含関係' :
                input.communityRelationship === 'rival' ? '敵対関係' :
                input.communityRelationship === 'ally' ? '同盟関係' :
                input.communityRelationship === 'trade' ? '交易関係' :
                input.communityRelationship === 'neighbor' ? '隣接関係' :
                input.communityRelationship === 'colony' ? '植民地関係' :
                input.communityRelationship === 'overlord' ? '宗主国関係' :
                input.communityRelationship === 'historical' ? '歴史的関係' :
                input.communityRelationship === 'cultural' ? '文化的関係' :
                input.communityRelationship === 'religious' ? '宗教的関係' :
                'その他'
              }
            </p>
          )}
        </div>
      )}

      {/* コンテキストは内部的に保持するが表示しない */}

      {/* カスタム関係性説明 */}
      {input.communityRelationship === 'custom' && (
        <div className="space-y-2">
          <Label htmlFor="custom-relationship">関係性の詳細説明</Label>
          <Textarea
            id="custom-relationship"
            value={input.additionalRequirements || ''}
            onChange={(e) => onChange({ ...input, additionalRequirements: e.target.value })}
            placeholder="具体的な関係性を説明してください。例：過去に戦争をして現在は停戦中、貿易で栄えた姉妹都市、など"
            rows={3}
          />
        </div>
      )}
    </div>
  )
}